import React, { useState, useEffect, useCallback } from 'react';
import { Download, Search, FileText, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import api from '@/utils/api';

const fmtRound = (val) => Math.round(parseFloat(val || 0)).toLocaleString('en-IN');
const monthLabel = (m) => {
  if (!m) return '';
  const d = new Date(m + '-01');
  return `${d.toLocaleString('en-IN', { month: 'long' })}, ${m.split('-')[0]}`;
};

const SalarySlip = ({ user }) => {
  const [salaries, setSalaries] = useState([]);
  const [employees, setEmployees] = useState({});
  const [costCentres, setCostCentres] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [filterMonth, setFilterMonth] = useState(new Date().toISOString().slice(0, 7));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [salV2Res, salV1Res, empRes, ccRes] = await Promise.all([
        api.get('/salary-process-v2?source=all'),
        api.get('/salary-process'),
        api.get('/employees?status=Active'),
        api.get('/cost-centres'),
      ]);
      const empMap = {};
      empRes.data.forEach(e => { empMap[e.id] = e; });
      setEmployees(empMap);
      const ccMap = {};
      ccRes.data.forEach(c => { ccMap[c.id] = c.name; });
      setCostCentres(ccMap);
      // Merge V2 + V1 records, V2 takes priority by employee+month
      const v2Data = (Array.isArray(salV2Res.data) ? salV2Res.data : []).map(s => ({ ...s, _source: 'v2' }));
      const v1Data = (Array.isArray(salV1Res.data) ? salV1Res.data : []).map(s => ({ ...s, _source: 'v1' }));
      const v2Keys = new Set(v2Data.map(s => `${s.employee_id}_${s.month}`));
      const combined = [...v2Data, ...v1Data.filter(s => !v2Keys.has(`${s.employee_id}_${s.month}`))];
      const approved = combined.filter(s => {
        const ceoApproved = (s.ceo_status || '').toLowerCase() === 'approved';
        return ceoApproved && s.month === filterMonth;
      });
      setSalaries(approved);
      setSelectedIds([]);
    } catch (error) {
      toast.error('Failed to load data');
    } finally { setLoading(false); }
  }, [filterMonth]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = salaries.filter(s => {
    const nameMatch = !searchName || (s.employee_name || '').toLowerCase().includes(searchName.toLowerCase());
    return nameMatch;
  });

  const allSelected = filtered.length > 0 && filtered.every(s => selectedIds.includes(s.id));
  const toggleSelectAll = () => setSelectedIds(allSelected ? [] : filtered.map(s => s.id));
  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleDownloadSlips = async () => {
    if (selectedIds.length === 0) { toast.error('No employees selected'); return; }
    setProcessing(true);
    try {
      const response = await api.post('/salary-slip/generate', { salary_ids: selectedIds }, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Salary_Slips_${filterMonth}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`${selectedIds.length} salary slip(s) downloaded`);
    } catch (error) {
      toast.error('Failed to generate salary slips');
    }
    setProcessing(false);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div></div>;
  }

  const month = salaries.length > 0 ? monthLabel(salaries[0].month) : monthLabel(filterMonth);

  return (
    <div className="space-y-5" data-testid="salary-slip-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Salary Slips</h1>
          <p className="mt-1 text-base text-slate-600">
            Generate and download salary slips for approved salaries
            {month && <span className="ml-2 text-indigo-600 font-semibold">— {month}</span>}
          </p>
        </div>
        {selectedIds.length > 0 && (
          <Button onClick={handleDownloadSlips} disabled={processing} className="bg-indigo-600 hover:bg-indigo-700" data-testid="download-slips-button">
            <Download className="h-4 w-4 mr-1.5" />
            {processing ? 'Generating...' : `Download ${selectedIds.length} Slip(s)`}
          </Button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-end gap-4 flex-wrap">
        <div className="min-w-[160px]">
          <label className="text-xs font-medium text-slate-600 mb-1 block">Month</label>
          <Input type="month" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} className="w-44" data-testid="slip-filter-month" />
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="text-xs font-medium text-slate-600 mb-1 block">Search by Name</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              placeholder="Type employee name..."
              className="pl-9"
              data-testid="slip-search-name"
            />
          </div>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
          <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900">{salaries.length === 0 ? 'No Approved Salaries' : 'No matching records'}</h3>
          <p className="text-slate-600 mt-2">{salaries.length === 0 ? 'No approved salary records for this month.' : 'Try a different search.'}</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="slip-table">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-800 text-white">
                  <th className="px-3 py-2.5 text-center">
                    <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll}
                      className="border-white data-[state=checked]:bg-indigo-500 data-[state=checked]:border-indigo-500"
                      data-testid="slip-select-all" />
                  </th>
                  <th className="px-2 py-2.5 text-center font-semibold">Sr</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Employee Name</th>
                  <th className="px-3 py-2.5 text-left font-semibold">Designation</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Gross</th>
                  <th className="px-3 py-2.5 text-right font-semibold text-red-300">Deductions</th>
                  <th className="px-3 py-2.5 text-right font-semibold text-emerald-300">Net Salary</th>
                  <th className="px-3 py-2.5 text-center font-semibold">Days</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((s, idx) => {
                  const emp = employees[s.employee_id] || {};
                  const isSelected = selectedIds.includes(s.id);
                  return (
                    <tr key={s.id} className={`hover:bg-slate-50 transition-colors ${isSelected ? 'bg-indigo-50/40' : ''}`} data-testid={`slip-row-${s.id}`}>
                      <td className="px-3 py-3 text-center">
                        <Checkbox checked={isSelected} onCheckedChange={() => toggleSelect(s.id)} data-testid={`slip-check-${s.id}`} />
                      </td>
                      <td className="px-2 py-3 text-center text-slate-500 font-mono">{idx + 1}</td>
                      <td className="px-3 py-3">
                        <p className="font-semibold text-slate-900">{(s.employee_name || '').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())} <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${s.source === 'grid2' ? 'bg-violet-100 text-violet-700' : 'bg-sky-100 text-sky-700'}`} data-testid={`sp-source-${s.id}`}>{s.source === 'grid2' ? 'SP1' : 'SP2'}</span></p>
                        <p className="text-[10px] text-slate-400 font-mono">{emp.employee_code || ''}</p>
                      </td>
                      <td className="px-3 py-3 text-slate-700">{emp.employee_type_name || '—'}</td>
                      <td className="px-3 py-3 text-right font-mono text-slate-700">{fmtRound(s.gross_salary)}</td>
                      <td className="px-3 py-3 text-right font-mono text-red-600">{fmtRound(s.total_deductions)}</td>
                      <td className="px-3 py-3 text-right font-mono font-bold text-slate-900 text-sm">{fmtRound(s.net_salary)}</td>
                      <td className="px-3 py-3 text-center text-slate-600">{s.days_present}/{s.total_days}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-slate-100 font-semibold text-xs border-t-2 border-slate-300">
                  <td className="px-3 py-3" colSpan={4}>TOTAL ({filtered.length} employees)</td>
                  <td className="px-3 py-3 text-right font-mono">{fmtRound(filtered.reduce((s, r) => s + (r.gross_salary || 0), 0))}</td>
                  <td className="px-3 py-3 text-right font-mono text-red-600">{fmtRound(filtered.reduce((s, r) => s + (r.total_deductions || 0), 0))}</td>
                  <td className="px-3 py-3 text-right font-mono font-bold text-sm">{fmtRound(filtered.reduce((s, r) => s + (r.net_salary || 0), 0))}</td>
                  <td className="px-3 py-3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalarySlip;
